import { createContext, useState, useEffect } from "react";

export const SurveyContext = createContext();
const DB_NAME = "SurveyDB";
const STORE_NAME = "files";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const saveFileToIndexedDB = async (key, file) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(file, key);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const getFileFromIndexedDB = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const deleteFileFromIndexedDB = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(key);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const SurveyProvider = ({ children }) => {
  const [surveyData, setSurveyData] = useState(null);
  const [surveyMeta, setSurveyMeta] = useState(null);   
  const [audienceData, setAudienceData] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      // Load survey file from IndexedDB
      try {
        const savedSurveyFile = await getFileFromIndexedDB("surveyFile");
        if (savedSurveyFile) {
          setSurveyData(savedSurveyFile);
        }
      } catch (err) {
        console.error("Error loading survey from IndexedDB:", err);
      }
      const savedSurveyMeta = localStorage.getItem("surveyMeta");
      const savedAudience = localStorage.getItem("audienceData");

      if (savedSurveyMeta) setSurveyMeta(JSON.parse(savedSurveyMeta));
      if (savedAudience) setAudienceData(JSON.parse(savedAudience));
    };

    loadData();
  }, []);

  useEffect(() => {
    const saveData = async () => {
      if (surveyData) {
        try {
          // Save File object to IndexedDB
          await saveFileToIndexedDB("surveyFile", surveyData);
        } catch (err) {
          console.error("Error saving survey to IndexedDB:", err);
        }
      }
    };

    saveData();
  }, [surveyData]);

  useEffect(() => {
    if (surveyMeta) {
      localStorage.setItem("surveyMeta", JSON.stringify(surveyMeta));
    }
  }, [surveyMeta]);

  useEffect(() => {
    if (audienceData && audienceData.length > 0) {
      localStorage.setItem("audienceData", JSON.stringify(audienceData));
    }
  }, [audienceData]);

  const clearSurvey = async () => {
    setSurveyData(null);
    setSurveyMeta(null);       
    setAudienceData([]);

    try {
      await deleteFileFromIndexedDB("surveyFile");
    } catch (err) {
      console.error("Error deleting survey from IndexedDB:", err);
    }

    localStorage.removeItem("surveyMeta");   
    localStorage.removeItem("audienceData");
  };

  return (
    <SurveyContext.Provider
      value={{
        surveyData,
        setSurveyData,
        surveyMeta,        
        setSurveyMeta,     
        audienceData,
        setAudienceData,
        clearSurvey
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

// Helper function to get survey data from storage
export const getSurveyDataFromStorage = async () => {
  try {
    const surveyFile = await getFileFromIndexedDB("surveyFile");
    const savedSurveyMeta = localStorage.getItem("surveyMeta");
    const savedAudience = localStorage.getItem("audienceData");
    
    return {
      surveyData: surveyFile || null,
      surveyMeta: savedSurveyMeta ? JSON.parse(savedSurveyMeta) : null,
      audienceData: savedAudience ? JSON.parse(savedAudience) : []
    };
  } catch (err) {
    console.error("Error getting survey data from storage:", err);
    return {
      surveyData: null,
      surveyMeta: null,
      audienceData: []
    };
  }
};